/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, February 11th 2022, 12:56:51 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */
import React from "react";
import Tabs, { TabsProps } from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { invariant } from "immutability-helper";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabbed-navigator-tab-panel-${index}`}
      aria-labelledby={`tabbed-navigator-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `tabbed-navigator-tab-${index}`,
    "aria-controls": `tabbed-navigator-tab-panel-${index}`,
  };
};

interface TabbedNavigatorProps extends Pick<TabsProps, "orientation" | "variant"> {
  children: React.ReactNode;
  onSelectTab?: (tabIndex: number) => void;
}

export const TabbedNavigator = ({ children, onSelectTab, orientation, variant }: TabbedNavigatorProps) => {
  invariant(
    React.Children.toArray(children).length % 2 === 0,
    () =>
      "Children of the TabbedNavigator must be provided as pairs. For each tab provide a set of 2 sequential elements i.e. the first one will be the tab title and the second will be the tab content e.g. <div>My Title</div><div>My Content</div>"
  );

  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    onSelectTab?.(newValue);
  };

  const tabTitleArray = React.Children.toArray(children).filter((child, index) => index % 2 === 0);
  const tabContentArray = React.Children.toArray(children).filter((child, index) => index % 2 !== 0);

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          orientation={orientation}
          value={value}
          onChange={(e, v) => handleChange(e, v)}
          aria-label="tabbed navigator"
          variant={variant ?? "scrollable"}
        >
          {tabTitleArray.map((titleChild, titleIndex) => (
            /** Key can be an index because it will never change unless code is changed */
            <Tab
              label={titleChild}
              {...a11yProps(titleIndex)}
              key={`tab-title-${titleIndex}`}
            /> /**Note: Tabs cannot take wrapped children so dont add a fragment or div here. https://github.com/mui/material-ui/issues/13267   https://mui.com/guides/composition/#wrapping-components */
          ))}
        </Tabs>
      </Box>
      {tabContentArray.map((contentChild, contentIndex) => (
        /** Key can be an index because it will never change unless code is changed */
        <React.Fragment key={`tab-content-${contentIndex}`}>
          <TabPanel value={value} index={contentIndex}>
            {contentChild}
          </TabPanel>
        </React.Fragment>
      ))}
    </>
  );
};
